import './SiteMonitoring.scss';
import { JSX, ReactNode, useCallback, useEffect, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { HiOutlinePlus } from 'react-icons/hi';
import { ConfigurationRow } from '@blisspointmedia/bpm-types/dist/SiteMonitoring';
import { Intent, Placement, Size } from '@Components/designConstants';
import { Button } from '../../Components/Button';
import { Dropdown, DropdownToggleType } from '../../Components/Dropdown';
import { Page } from '../../Components/Page';
import { FullPageSpinner } from '../../Components/Spinner';
import { BPMTable } from '../../Components/StickyTable';
import { useAppSelector } from '../../redux';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import { emailSelector } from '../../redux/user';
import { awaitJSON, SiteMonitoringLambdaFetch } from '../../utils/fetch-utils';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { VALID_URL_REGEXP } from '../../utils/url-utils';
import { FormValueKey, FormValues, FREQUENCIES, MonitorForm } from './MonitorForm';

const INNER_CELL_HEIGHT = 70;

const WIDTHS = {
  COMPANY: 300,
  PAGE_URL: 350,
  FREQUENCY: 200,
  SLACK_CHANNEL: 150,
  DRI: 200,
  ACTIONS: 100
};

const ACTION_OPTIONS = ['Update', 'Delete'] as const;
const TAB_OPTIONS = ['view', 'create', 'update'] as const;

type ActionOption = (typeof ACTION_OPTIONS)[number];
type TabOption = (typeof TAB_OPTIONS)[number];

const NoRows = () => (
  <div className='no-configurations' data-testid='no-configurations'>
    No data to show
  </div>
);

export const SiteMonitoring = (): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('');
  const [pageUrl, setPageUrl] = useState('');
  const [slackChannel, setSlackChannel] = useState('');
  const [dri, setDri] = useState('');
  const [frequency, setFrequency] = useState(FREQUENCIES[0]);
  const [configurations, setConfigurations] = useState<ConfigurationRow[]>([]);
  const [currentConfiguration, setCurrentConfiguration] = useState<ConfigurationRow>();

  const { tab, goToTab } = useTabbedNav<TabOption>({
    baseURL: 'site-monitoring',
    defaultKey: 'view',
    navs: TAB_OPTIONS as unknown as TabOption[] // casting to make mutable
  });

  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const userEmail = useAppSelector(emailSelector);

  useEffect(() => {
    const fetcher = async () => {
      try {
        const res = await SiteMonitoringLambdaFetch('/configurations');
        const { data } = await awaitJSON(res);
        setConfigurations(data);
      } catch (e) {
        setError({
          message: `Failed to fetch site monitor configurations. Error: ${e.message}`,
          reportError: e
        });
      }
      setLoading(false);
    };
    fetcher();
  }, [setError]);

  const resetForm = useCallback(() => {
    setCompany('');
    setSlackChannel('');
    setDri('');
    setFrequency(FREQUENCIES[0]);
    setPageUrl('');
  }, []);

  const onSelectUpdate = useCallback(
    (data: ConfigurationRow) => {
      setCurrentConfiguration(data);
      setCompany(data.company);
      setSlackChannel(data.slack_channel ?? '');
      setDri(data.dri ?? '');
      setFrequency(data.frequency ?? FREQUENCIES[0]);
      setPageUrl(data.page_url ?? '');
      goToTab('update');
    },
    [goToTab]
  );

  const deleteConfiguration = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const res = await SiteMonitoringLambdaFetch(`/configurations/${id}`, {
          method: 'DELETE'
        });
        const { data } = await awaitJSON(res);
        if (data.id === id && data.is_delete === true) {
          setConfigurations(prev => prev.filter(c => c.id !== id));
        }
      } catch (e) {
        setError({
          message: `Failed to delete site monitor configuration. Error: ${e.message}`,
          reportError: e
        });
      }
      setLoading(false);
    },
    [setError]
  );

  const onSelectDelete = useCallback(
    async (data: ConfigurationRow) => {
      try {
        await setAreYouSure({
          intent: Intent.ERROR,
          title: 'Delete Site Monitor',
          message: (
            <>
              <p>
                Do you really want to remove this record? Reactivating a deleted record is not
                possible.
              </p>
              <br />
              <p>
                Contains page URL: <strong>{data.page_url}</strong>
              </p>
            </>
          ),
          onOkay: () => deleteConfiguration(data.id)
        });
      } catch {
        // user cancelled — no action needed
      }
    },
    [deleteConfiguration, setAreYouSure]
  );

  const updateConfiguration = useCallback(
    async (id: string) => {
      if (!VALID_URL_REGEXP.test(pageUrl)) {
        setError({ message: 'Please enter a valid page URL, including HTTPS protocol' });
        return;
      }

      setLoading(true);
      goToTab('view');
      try {
        const res = await SiteMonitoringLambdaFetch(`/configurations/${id}`, {
          method: 'PATCH',
          body: { page_url: pageUrl, slack_channel: slackChannel, dri, frequency },
          params: { email: userEmail }
        });
        const { data } = await awaitJSON(res);
        if (data.id === id && data.is_delete === false) {
          setConfigurations(prev => [data, ...prev.filter(c => c.id !== id)]);
        }
      } catch (e) {
        setError({
          message: `Failed to update site monitor configuration. Error: ${e.message}`,
          reportError: e
        });
      }
      setLoading(false);
      resetForm();
    },
    [dri, frequency, goToTab, pageUrl, resetForm, setError, slackChannel, userEmail]
  );

  const createConfiguration = useCallback(async () => {
    if (!VALID_URL_REGEXP.test(pageUrl)) {
      setError({ message: 'Please enter a valid page URL, including HTTPS protocol' });
      return;
    }

    setLoading(true);
    goToTab('view');
    try {
      const res = await SiteMonitoringLambdaFetch('/configurations', {
        method: 'POST',
        body: { company, page_url: pageUrl, slack_channel: slackChannel, dri, frequency },
        params: { email: userEmail }
      });
      const { data } = await awaitJSON(res);
      if (data.is_delete === false) {
        setConfigurations(prev => [data, ...prev]);
      }
    } catch (e) {
      setError({
        message: `Failed to create site monitor configuration. Error: ${e.message}`,
        reportError: e
      });
    }
    setLoading(false);
    resetForm();
  }, [company, dri, frequency, goToTab, pageUrl, resetForm, setError, slackChannel, userEmail]);

  const handleFormValueChange = useCallback((key: keyof FormValues, value: string) => {
    if (key === FormValueKey.COMPANY) {
      setCompany(value);
    } else if (key === FormValueKey.PAGE_URL) {
      setPageUrl(value);
    } else if (key === FormValueKey.SLACK_CHANNEL) {
      setSlackChannel(value);
    } else if (key === FormValueKey.DRI) {
      setDri(value);
    } else if (key === FormValueKey.FREQUENCY) {
      setFrequency(value);
    }
  }, []);

  const formValues: FormValues = { company, pageUrl, slackChannel, dri, frequency };

  const configurationsHeaders = [
    {
      label: 'Company',
      name: 'company',
      type: 'text',
      flex: true,
      minFlexWidth: WIDTHS.COMPANY,
      renderer: (data: ConfigurationRow) => <TableCell data={data.company} />
    },
    {
      label: 'Page URL',
      name: 'page_url',
      type: 'text',
      flex: true,
      minFlexWidth: WIDTHS.PAGE_URL,
      renderer: (data: ConfigurationRow) => <TableCell data={data.page_url} />
    },
    {
      label: 'Frequency',
      name: 'frequency',
      type: 'text',
      flex: true,
      minFlexWidth: WIDTHS.FREQUENCY,
      renderer: (data: ConfigurationRow) => <TableCell data={data.frequency} />
    },
    {
      label: 'Slack Channel',
      name: 'slack_channel',
      type: 'text',
      flex: true,
      minFlexWidth: WIDTHS.SLACK_CHANNEL,
      renderer: (data: ConfigurationRow) => <TableCell data={data.slack_channel} />
    },
    {
      label: 'DRI Slack Username',
      name: 'dri',
      type: 'text',
      flex: true,
      minFlexWidth: WIDTHS.DRI,
      renderer: (data: ConfigurationRow) => <TableCell data={data.dri} />
    },
    {
      label: 'Actions',
      flex: true,
      minFlexWidth: WIDTHS.ACTIONS,
      renderer: (data: ConfigurationRow) => (
        <div data-testid='actions-dropdown'>
          <Dropdown
            type={DropdownToggleType.ICON_ONLY}
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            className='actions-dropdown'
            options={ACTION_OPTIONS}
            onChange={(action: ActionOption) => {
              if (action === 'Update') {
                onSelectUpdate(data);
              } else {
                onSelectDelete(data);
              }
            }}
            leadingIcon={<BsThreeDotsVertical />}
            placeholder=''
          />
        </div>
      )
    }
  ];

  const renderContent = () => {
    if (loading) return <FullPageSpinner />;
    if (tab !== 'view') {
      return (
        <MonitorForm
          mode={tab}
          values={formValues}
          currentConfiguration={currentConfiguration}
          onValueChange={handleFormValueChange}
          onCancel={() => {
            resetForm();
            goToTab('view');
          }}
          onCreate={createConfiguration}
          onUpdate={() => {
            if (currentConfiguration) {
              updateConfiguration(currentConfiguration.id);
            }
          }}
        />
      );
    }
    return (
      <BPMTable
        data={configurations}
        headers={configurationsHeaders}
        noRowsRenderer={NoRows}
        rowHeight={INNER_CELL_HEIGHT}
        pagination
      />
    );
  };

  return (
    <Page
      title='Site Monitoring'
      pageType='Site Monitoring'
      actions={
        tab === 'view' && !loading ? (
          <Button
            size={Size.LG}
            leadingElement={<HiOutlinePlus />}
            onClick={() => goToTab('create')}
          >
            Add Site Monitor
          </Button>
        ) : undefined
      }
    >
      <div className='site-monitoring'>{renderContent()}</div>
    </Page>
  );
};

// Private sub-components

const TableCell = ({ data }: { data: ReactNode }) => (
  <div className='table-cell' title={typeof data === 'string' ? data : undefined}>
    {data}
  </div>
);
